import { type RefObject, useEffect, type ReactNode } from 'react';
import { motion } from 'framer-motion';
import { useHistory } from 'react-router-dom';
import { useScrollToTopFixIOS } from '@hungryroot/environment';
import { Button } from '@hungryroot/ramen';
import {
  getCardAnimation,
  type ValuePropComponentTheme,
} from '../../defs/valueProp';
import { setSavedNavState } from '../../navigation/navigation';
import { Footer } from '../Footer/Footer';
import { ValuePropHeader } from '../ValuePropHeader/ValuePropHeader';
import styles from './ValuePropLayout.module.css';

interface Props {
  headerText: string;
  theme: ValuePropComponentTheme;
  cards: ReactNode[];
  next: string | null;
  prev: string | null;
  scrollRef: RefObject<HTMLDivElement>;
}

export function ValuePropLayout({
  headerText,
  theme,
  next,
  prev,
  cards,
  scrollRef,
}: Props) {
  const history = useHistory();
  const { containerAnimation, cardAnimation } = getCardAnimation(0.45);

  useScrollToTopFixIOS(scrollRef);

  useEffect(() => {
    if (next !== null) {
      setSavedNavState(next);
    }
  }, [next]);

  return (
    <div className={styles.container}>
      <ValuePropHeader
        theme={theme}
        heading={headerText}
        headingClassName={styles.heading}
      />
      <motion.div
        className={styles.content}
        variants={containerAnimation}
        initial="hidden"
        animate="show"
      >
        {cards.map((card, i) => (
          <motion.div key={i} variants={cardAnimation}>
            {card}
          </motion.div>
        ))}
      </motion.div>

      <Footer
        className={styles.footer}
        backButton={
          prev !== null && (
            <Button
              className={styles[`back-${theme}`]}
              onClick={(e) => {
                e.preventDefault();
                history.push(prev);
              }}
              style="default"
              size="medium"
              prominence="secondary"
            >
              Back
            </Button>
          )
        }
        submitButton={
          <Button
            className={styles[`submit-${theme}`]}
            buttonType="submit"
            testid="quiz-button-next"
            onClick={() => {
              if (next !== null) {
                history.push(next);
              }
            }}
            style="default"
            size="medium"
            prominence="primary"
          >
            Continue
          </Button>
        }
      />
    </div>
  );
}
